import { mmToPx } from '../../shared/units'
import type { CanvasElement } from '../entities/element'

/**
 * Element Bounds Calculator Service
 * Calculates visual bounds for canvas elements
 */

export type ElementBounds = {
  x: number
  y: number
  width: number
  height: number
}

export const calculateElementBounds = (
  element: CanvasElement & { position: { x: number; y: number } },
  exportZone: { left: number; top: number },
): ElementBounds => {
  // Element position is relative to export zone (0,0 is top-left of export zone)
  // Export zone coordinates are absolute (relative to background)
  const x = exportZone.left + mmToPx(element.position.x)
  const y = exportZone.top + mmToPx(element.position.y)

  if (element.type === 'text') {
    // Text position is now center/center
    const fontSize = mmToPx(element.fontSize || 16)
    const lines = (element.content || '').split('\n')
    const lineHeight = 1.2 // Standard line height multiplier

    // Calculate width based on longest line
    const longestLineLength = Math.max(...lines.map((line) => line.length))
    const width = Math.max(100, longestLineLength * fontSize * 0.6) // rough approximation

    // Calculate height based on number of lines
    const height = fontSize * lineHeight * lines.length

    // Center the bounds around the text center position
    return {
      x: x - width / 2,
      y: y - height / 2,
      width,
      height,
    }
  } else if (element.type === 'asset' || element.type === 'image') {
    // For assets and images, use actual dimensions from the element
    const baseWidth = mmToPx(element.dimensions?.width || 50)
    const baseHeight = mmToPx(element.dimensions?.height || 50)

    // Apply scale transform if present (use absolute values for bounds)
    const scaleX = Math.abs(element.transform?.scaleX || 1)
    const scaleY = Math.abs(element.transform?.scaleY || 1)
    const width = baseWidth * scaleX
    const height = baseHeight * scaleY

    // Assets/images are now positioned by their center (consistent with text)
    // So center the bounds around the element position
    return {
      x: x - width / 2,
      y: y - height / 2,
      width,
      height,
    }
  } else if (element.type === 'shape') {
    // For shapes, use their dimensions
    const width = mmToPx(element.dimensions.width)
    const height = mmToPx(element.dimensions.height)
    return {
      x: x - width / 2,
      y: y - height / 2,
      width,
      height,
    }
  }

  // Default fallback
  return { x, y, width: 100, height: 100 }
}
